import { TrendingUp, TrendingDown, Minus, BarChart2, GitBranch, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { DeepAnalysis } from '@/hooks/useDeepAnalysis';

interface AnalysisSectionProps {
  analysis: DeepAnalysis;
}

const getTrendIcon = (trend: string) => {
  if ((trend || '').toLowerCase().includes('bullish') || (trend || '').toLowerCase().includes('up')) {
    return <TrendingUp className="w-4 h-4 text-success" />;
  }
  if ((trend || '').toLowerCase().includes('bearish') || (trend || '').toLowerCase().includes('down')) {
    return <TrendingDown className="w-4 h-4 text-destructive" />;
  }
  return <Minus className="w-4 h-4 text-warning" />;
};

const getGradeColor = (grade: string) => {
  switch ((grade || '').toUpperCase()) {
    case 'A': return 'bg-success text-success-foreground';
    case 'B': return 'bg-success/70 text-success-foreground';
    case 'C': return 'bg-warning text-warning-foreground';
    case 'D': return 'bg-destructive/70 text-destructive-foreground';
    case 'F': return 'bg-destructive text-destructive-foreground';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-success';
  if (score >= 50) return 'text-warning';
  return 'text-destructive';
};

const formatUsd = (price: number | null | undefined) => {
  if (price === null || price === undefined || !Number.isFinite(price)) return 'Unavailable';
  if (price < 0.01) return `US$${price.toFixed(6)}`;
  if (price < 1) return `US$${price.toFixed(4)}`;
  return `US$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatIndicatorPrice = (price: number | null | undefined, currency?: string) => {
  if (price === null || price === undefined || !Number.isFinite(price)) return 'Unavailable';
  const prefix = currency === 'aud' ? 'A$' : 'US$';
  if (price < 0.01) return `${prefix}${price.toFixed(6)}`;
  if (price < 1) return `${prefix}${price.toFixed(4)}`;
  return `${prefix}${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const rsiCopy = (value: number | null | undefined, signal?: string) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Insufficient candle history for RSI calculation';
  if (signal === 'oversold') return `RSI at ${value.toFixed(1)} — this token is in oversold territory, which has historically preceded recoveries in similar assets`;
  if (signal === 'overbought') return `RSI at ${value.toFixed(1)} — momentum is extended; watch for consolidation before the next leg`;
  return `RSI at ${value.toFixed(1)} — no extreme momentum signal currently present`;
};

const maCopy = (indicators: DeepAnalysis['historicalIndicators']) => {
  if (!indicators || indicators.ma_50 === null || indicators.ma_200 === null) return 'Moving average data requires additional price history';
  const above50 = indicators.price_vs_ma_50 === 'above';
  const above200 = indicators.price_vs_ma_200 === 'above';
  if (above50 && above200) return 'Trading above both key moving averages — trend structure is constructive';
  if (!above50 && !above200) return 'Below both key moving averages — no confirmed trend recovery yet';
  return `Trading ${above50 ? 'above' : 'below'} MA_50 and ${above200 ? 'above' : 'below'} MA_200 — trend structure is mixed`;
};

export function AnalysisSection({ analysis }: AnalysisSectionProps) {
  const indicators = analysis.historicalIndicators;
  const technical = analysis.technicalAnalysis || { score: 0, trend: 'Neutral', keyLevels: { support: 0, resistance: 0 }, signals: [], analysis: 'Technical analysis is temporarily incomplete.' };
  const fundamental = analysis.fundamentalAnalysis || { score: 0, grade: 'N/A', strengths: [], weaknesses: [], analysis: 'Fundamental analysis is temporarily incomplete.' };
  const sentiment = analysis.sentimentAnalysis || { score: 0, overall: 'Neutral', socialBuzz: 'Low', keyDrivers: [], analysis: 'Sentiment analysis is temporarily incomplete.' };
  const supports = indicators?.support_levels || [];
  const resistances = indicators?.resistance_levels || [];
  const indicatorCurrency = analysis.historicalAvailability?.currency || indicators?.currency || 'usd';
  const sourceLabel = analysis.historicalAvailability?.source === 'derived_stored_history' || indicators?.data_source === 'derived_stored_history'
    ? 'Derived from stored market history'
    : analysis.historicalAvailability?.partial
      ? 'Partial analytics shown'
      : 'Historical analytics';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Technical Analysis */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Technical Analysis</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {getTrendIcon(technical.trend)}
              <span className={`font-bold ${getScoreColor(technical.score)}`}>
                {technical.score}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Progress value={technical.score} className="h-2" />
          
          <div className="text-sm">
            <span className="text-muted-foreground">Trend: </span>
            <span className="font-medium text-foreground">{technical.trend}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="p-2 bg-success/10 rounded">
              <span className="text-xs text-muted-foreground block">Support</span>
              <span className="font-bold text-success">{formatIndicatorPrice(technical.keyLevels?.support, 'aud')}</span>
            </div>
            <div className="p-2 bg-destructive/10 rounded">
              <span className="text-xs text-muted-foreground block">Resistance</span>
              <span className="font-bold text-destructive">{formatIndicatorPrice(technical.keyLevels?.resistance, 'aud')}</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Signals</span>
            <div className="flex flex-wrap gap-1">
              {(technical.signals || []).length > 0 ? technical.signals.map((signal, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {signal}
                </Badge>
              )) : <span className="text-xs text-muted-foreground">No confirmed technical signals yet</span>}
            </div>
          </div>

          <div className="space-y-2 rounded border border-border bg-secondary/20 p-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="w-fit text-[10px]">{sourceLabel}</Badge>
            <p>{rsiCopy(indicators?.rsi_value, indicators?.rsi_signal)}</p>
            <p>MA_50: {formatIndicatorPrice(indicators?.ma_50, indicatorCurrency)} · MA_200: {formatIndicatorPrice(indicators?.ma_200, indicatorCurrency)}</p>
            <p>{maCopy(indicators)}</p>
            <p>
              {supports.length
                ? `Support identified near ${supports.map((level) => formatIndicatorPrice(level, indicatorCurrency)).join(' and ')} based on available price structure`
                : 'No clear structural levels identified in the available history'}
            </p>
            <p>
              {resistances.length
                ? `Resistance identified near ${resistances.map((level) => formatIndicatorPrice(level, indicatorCurrency)).join(' and ')} based on available price structure`
                : 'No clear structural levels identified in the available history'}
            </p>
            <p className="border-t border-border pt-2 text-[11px]">Technical indicators are {indicatorCurrency === 'aud' ? 'derived from stored AUD market history' : 'calculated in USD'}. Market data displayed in AUD.</p>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-2">
            {technical.analysis}
          </p>
        </CardContent>
      </Card>

      {/* Fundamental Analysis */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Fundamentals</CardTitle>
            </div>
            <Badge className={getGradeColor(fundamental.grade)}>
              Grade {fundamental.grade}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Score</span>
            <span className={`font-bold ${getScoreColor(fundamental.score)}`}>
              {fundamental.score}/100
            </span>
          </div>
          <Progress value={fundamental.score} className="h-2" />

          <div className="space-y-2">
            <div>
              <span className="text-xs text-success font-medium">Strengths</span>
              <ul className="mt-1 space-y-1">
                {(fundamental.strengths || []).length > 0 ? fundamental.strengths.map((s, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                    <span className="text-success">+</span> {s}
                  </li>
                )) : <li className="text-xs text-muted-foreground">No clear strengths identified from available data.</li>}
              </ul>
            </div>
            <div>
              <span className="text-xs text-destructive font-medium">Weaknesses</span>
              <ul className="mt-1 space-y-1">
                {(fundamental.weaknesses || []).length > 0 ? fundamental.weaknesses.map((w, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                    <span className="text-destructive">-</span> {w}
                  </li>
                )) : <li className="text-xs text-muted-foreground">No specific weaknesses returned in this review.</li>}
              </ul>
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-2">
            {fundamental.analysis}
          </p>
        </CardContent>
      </Card>

      {/* Sentiment Analysis */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Sentiment</CardTitle>
            </div>
            <Badge variant="outline">
              {sentiment.overall}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Score</span>
            <span className={`font-bold ${getScoreColor(sentiment.score)}`}>
              {sentiment.score}/100
            </span>
          </div>
          <Progress value={sentiment.score} className="h-2" />

          <div className="flex items-center justify-between text-sm p-2 bg-secondary/30 rounded">
            <span className="text-muted-foreground">Social Buzz</span>
            <Badge variant={sentiment.socialBuzz === 'High' ? 'default' : 'secondary'}>
              {sentiment.socialBuzz}
            </Badge>
          </div>

          <div>
            <span className="text-xs text-muted-foreground">Key Drivers</span>
            <ul className="mt-1 space-y-1">
              {(sentiment.keyDrivers || []).length > 0 ? sentiment.keyDrivers.map((d, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                  <span className="text-primary">•</span> {d}
                </li>
              )) : <li className="text-xs text-muted-foreground">No strong sentiment drivers detected from available data.</li>}
            </ul>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-2">
            {sentiment.analysis}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
